<script setup lang="ts">
import { toRefs, computed, ref, nextTick, watchEffect } from 'vue';
import type { ShowResultsMessage, ScrubbedQuestion } from '@ilteducation/quizter-types';
import { useScoreAnimation } from '../../composables/useScoreAnimation';
import { groupByPosition } from '../../util/sortResults';
import { useQuizterStore } from '../../stores/quizterStore';
import { storeToRefs } from 'pinia';
import useLocale from '../../../composables/use-locale';

type Props = {
  results: ShowResultsMessage['payload']['results'];
  currentCorrectAnswer?: string;
  currentQuestion?: ScrubbedQuestion;
};

const props = defineProps<Props>();

const { t } = useLocale();

// Extract reactive refs from props
const { results } = toRefs(props);

// Use composable for animation
const { sortedResults, isAnimating } = useScoreAnimation(results);

// Get store to check if it's the first question
const store = useQuizterStore();
const isFirstQuestion = computed(() => store.currentQuestionIndex === 0);
const { joinedPlayers, gameOptions, isGameFinished } = storeToRefs(store);

const playerAvatarUrl = (playerId: string) =>
  joinedPlayers.value?.find((jp) => jp.id.toString() === playerId)?.avatar.image;

// Group players by position to handle ties, then take top 5 positions
const groupedLeaderboard = computed(() => groupByPosition(sortedResults.value).slice(0, 5));

const headingRefGameFinished = ref<HTMLElement | null>(null);
const headingRef = ref<HTMLElement | null>(null);
watchEffect(async () => {
  if (isGameFinished.value) {
    await nextTick();
    headingRefGameFinished.value?.focus();
  } else {
    headingRef.value?.focus();
  }
});
</script>

<template>
  <v-container class="py-0">
    <h1
      v-if="!isGameFinished"
      ref="headingRef"
      tabindex="-1"
      class="chalkboard-font text-white text-center text-h4 mb-6"
    >
      {{ t('results.leaderboard_title') }}
    </h1>
    <h1 v-else ref="headingRefGameFinished" tabindex="-1" class="d-sr-only">
      {{ t('results.final_results_title') }}
    </h1>

    <TransitionGroup name="leaderboard-list" tag="div" class="pa-0">
      <v-list-item v-for="group in groupedLeaderboard" :key="group.position" class="mb-3 rounded-lg leaderboard-item">
        <template v-slot:prepend>
          <v-chip
            color="white"
            class="text-on-surface chalkduster-font font-weight-bold mr-4 position-chip justify-center"
          >
            {{ group.position }}
          </v-chip>
        </template>

        <template #title>
          <div class="d-flex align-center justify-space-between w-100">
            <!-- Left side: Avatars -->
            <div class="d-flex align-center ga-2">
              <v-avatar v-for="player in group.players.slice(0, 3)" :key="player.player" size="60" rounded="0">
                <v-img
                  v-if="player.player"
                  :src="playerAvatarUrl(player.player.toString())"
                  alt="Player avatar"
                ></v-img>
                <v-icon v-else color="white" icon="fa:fawsb fa-user"></v-icon>
              </v-avatar>
              <span v-if="group.players.length > 3" class="text-white ml-2">
                + {{ group.players.length - 3 }} {{ t('results.other_players') }}
              </span>
            </div>

            <!-- Right side: -->
            <div class="d-flex align-center ml-auto right-side-container">
              <!-- Plus points - only shown when timer is enabled -->
              <div class="plus-points-container leaderboard-item--text">
                <span v-if="gameOptions.useTimer && group.lastQuestionPoints > 0" class="score-change-text">
                  (+{{ group.lastQuestionPoints }})
                </span>
              </div>

              <!-- Score -->
              <div class="score-container">
                <span class="leaderboard-item--text"> {{ group.score }} {{ t('results.points') }} </span>
              </div>

              <!-- Arrow  -->
              <div class="position-change-indicator">
                <v-icon
                  v-if="
                    gameOptions.useTimer &&
                    !isAnimating &&
                    !isFirstQuestion &&
                    group.score > 0 &&
                    group.previousPosition &&
                    group.previousPosition > group.position
                  "
                  size="large"
                  icon="fa:fawsb fa-arrow-up"
                  class="position-up-icon"
                ></v-icon>
                <v-icon
                  v-if="
                    gameOptions.useTimer &&
                    !isAnimating &&
                    !isFirstQuestion &&
                    group.score > 0 &&
                    group.previousPosition &&
                    group.previousPosition < group.position
                  "
                  size="large"
                  icon="fa:fawsb fa-arrow-down"
                  class="position-down-icon"
                ></v-icon>
              </div>
            </div>
          </div>
        </template>
      </v-list-item>
    </TransitionGroup>

    <v-card v-if="groupedLeaderboard.length === 0" variant="outlined" class="text-center py-8" rounded="lg">
      <v-card-text>
        <p class="text-white text-body-1 ma-0">{{ t('results.no_results') }}</p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped lang="scss">
.leaderboard-card {
  :deep(.v-list) {
    max-height: 500px;
    overflow-y: auto;
  }
}

.position-chip {
  width: 48px;
  height: 48px;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.position-up-icon {
  color: rgba(38, 250, 77, 1);
}

.position-down-icon {
  color: rgba(253, 78, 78, 1);
}

.score-change-text {
  color: rgba(2, 182, 35, 1);
}

// TransitionGroup animations for smooth position changes
.leaderboard-item {
  background-color: rgba(148, 153, 158, 0.2);
  transition: all 0.5s ease;
  &--text {
    font-size: 1.25rem;
  }

  :deep(.v-list-item-title) {
    width: 100%;
  }
}

.right-side-container {
  gap: 8px;
}

.position-change-indicator {
  min-width: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.plus-points-container {
  min-width: 60px;
  width: 60px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
}

.score-container {
  min-width: 110px;
  width: 110px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
}

.leaderboard-list-move {
  transition: transform 0.5s ease;
}

.leaderboard-list-enter-active,
.leaderboard-list-leave-active {
  transition: all 0.5s ease;
}

.leaderboard-list-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.leaderboard-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

[tabindex='-1']:focus {
  outline: none;
}
</style>
